import type { Dayjs } from "dayjs";
import { styled } from "@pigment-css/react";
import { ChevronRightThin } from "@acto/web-common/components";
import { RowBody, RowItem, RowMeta, RowTitle } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { capitalize, formatDayMonth, formatRelativeDays } from "@acto/taskpane/field-sales-2/utils/format";

const Row = styled(RowItem)(({ theme }) => ({
  paddingTop: theme.fieldSalesTwo.spacing[16],
}));

const ClickableRow = styled("button")(({ theme }) => ({
  flex: "1 0 0",
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  border: "none",
  background: "transparent",
  font: "inherit",
  color: "inherit",
  textAlign: "inherit",
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  borderRadius: theme.fieldSalesTwo.radius[16],
  paddingInline: theme.fieldSalesTwo.spacing[12],
  marginInline: `-${theme.fieldSalesTwo.spacing[12]}`,
  paddingBlock: theme.fieldSalesTwo.spacing[8],
  marginBlock: `-${theme.fieldSalesTwo.spacing[8]}`,
  transition: "background 120ms ease, transform 120ms ease",
  "&:hover": {
    background: theme.fieldSalesTwo.colors.surface.glassStrong,
    backgroundColor: "rgba(19, 19, 19, 0.04)",
  },
  ...pressInteraction(theme),
}));

const RowInfo = styled(RowBody)(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[4]} 0`,
}));

const Title = styled(RowTitle)({
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const Meta = styled(RowMeta)(({ theme }) => ({
  fontSize: theme.fieldSalesTwo.font.size["14"],
}));

const RowDate = styled(Meta)({
  flexShrink: 0,
  textAlign: "right",
  whiteSpace: "nowrap",
});

const Chevron = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const EMPTY_VALUE = "-";

export interface KpiRowProps {
  title: string;
  date: Dayjs | undefined;
  locale: string;
  onClick?: () => void;
}

export function KpiRow({ title, date, locale, onClick }: KpiRowProps) {
  const isoDate = date?.format("YYYY-MM-DD");
  const isClickable = Boolean(onClick);

  const content = (
    <>
      <RowInfo>
        <Title>{title}</Title>
        <Meta>{isoDate ? capitalize(formatRelativeDays(isoDate, locale)) : EMPTY_VALUE}</Meta>
      </RowInfo>
      <RowDate>{isoDate ? formatDayMonth(isoDate, locale) : EMPTY_VALUE}</RowDate>
      {isClickable && (
        <Chevron aria-hidden="true">
          <ChevronRightThin />
        </Chevron>
      )}
    </>
  );

  return (
    <Row>
      {isClickable ? (
        <ClickableRow type="button" onClick={onClick}>
          {content}
        </ClickableRow>
      ) : (
        content
      )}
    </Row>
  );
}
